// AUTO-BUNDLED from /app/docs/HELP_ZH.md and /app/docs/HELP_EN_SALES.md

export const HELP_ZH = `# BetterHome Inventory 系统 —— 用户帮助手册（中文版）

> 版本：2026-02  
> 适用对象：所有用户角色（Admin / Office / Sales / GT Sales / Customer）  
> 语言：简体中文

---

## 目录

1. [系统简介](#一系统简介)
2. [登录与账户](#二登录与账户)
3. [角色与权限概览](#三角色与权限概览)
4. [产品仪表盘 Dashboard](#四产品仪表盘-dashboard)
5. [购物车 / 报价管理 Carts & Quotes](#五购物车--报价管理-carts--quotes)
6. [客户管理 Customer Management](#六客户管理-customer-management)
7. [Giant Tiger 专属功能](#七giant-tiger-专属功能)
8. [中国报价系统联动（China Quote）](#八中国报价系统联动china-quote)
9. [移动端与条码扫描](#九移动端与条码扫描)
10. [邮件报价与公开报价页](#十邮件报价与公开报价页)
11. [数据同步 Sync & 备份 Backup](#十一数据同步-sync--备份-backup)
12. [用户管理 User Management](#十二用户管理-user-management)
13. [个人设置 Settings](#十三个人设置-settings)
14. [常见问题 FAQ](#十四常见问题-faq)

---

## 一、系统简介

BetterHome Inventory 是一个连接 FileMaker 服务器的 Web 应用，提供：

- **本地缓存**：从 FileMaker 每日两次自动同步产品数据到本地 MongoDB，加快查询速度。
- **多购物车 / 报价**：每位用户可维护多个独立的报价单，用于不同客户或场景。
- **PDF / Excel 导出**：Quotation、Proforma Invoice、GT Offer Sheet 一键生成。
- **RBAC 权限控制**：不同角色只能看到和操作自己权限内的功能。
- **PWA 支持**：可在手机主屏幕安装，支持扫码等移动场景。

---

## 二、登录与账户

1. 打开系统登录页 \`/login\`。
2. 输入管理员分配的邮箱和密码。
3. 登录成功后进入 Dashboard。
4. **忘记密码**：点击登录页 "Forgot password?"，填写邮箱，系统会通过 Resend 发送重置链接（60 分钟有效，一次性使用）。
5. **登出**：点击右上角头像 → "Logout"。

> **安全提示**：连续 5 次登录失败将触发 15 分钟锁定；请勿在公共电脑保留登录状态。

---

## 三、角色与权限概览

| 功能模块 | Admin | Office | Sales | GT Sales | Customer |
|---|:---:|:---:|:---:|:---:|:---:|
| 浏览 / 搜索产品 | ✅ | ✅ | ✅ | ✅ | ✅ |
| 编辑 / 新建产品 | ✅ | ✅ | ❌ | ❌ | ❌ |
| Block / 屏蔽产品 | ✅ | ✅ | ❌ | ❌ | ❌ |
| 多购物车 | ✅ | ✅ | ✅ | ✅ | ✅ |
| 编辑价格系数 Price Coefficient | ✅ | ✅ | ✅ | ✅ | ❌ |
| 删除购物车项 | ✅ | ✅ | ❌ | ❌ | ❌ |
| Quotation / Proforma 导出 | ✅ | ✅ | ✅ | ✅ | ✅（含系数价） |
| GT SKU / GT Selling History | ✅ | ✅ | ❌ | ✅ | ❌ |
| GT Offer Sheet 导出 | ✅ | ✅ | ❌ | ✅ | ❌ |
| China Quote（RMB 成本）查看 | ✅ | ✅ | ❌ | ❌ | ❌ |
| 客户 Customer 管理 | ✅ | ✅ | ❌ | ❌ | ❌ |
| 邮件发送报价 | ✅ | ✅ | ✅ | ✅ | ❌ |
| 用户管理 Users | ✅ | ✅ | ❌ | ❌ | ❌ |
| 数据同步 / 备份 | ✅ | ✅ | ❌ | ❌ | ❌ |

---

## 四、产品仪表盘 Dashboard

登录后默认进入 Dashboard，展示 "main products" 缓存数据。

### 4.1 视图切换
- **List View（列表视图）**：展示所有字段的可排序表格，适合大量比对。
- **Grid View（网格视图）**：卡片式展示，含产品图，适合销售演示。

### 4.2 搜索与筛选
- **搜索框**：可搜索 Item Number、Description、Barcode、GT SKU（GT SKU 仅对 Admin/Office/GT Sales 可搜索）。
- **左侧筛选栏 Sidebar**：按 Category、Best Selling、Blocked、Inventory Type 等过滤。
- **顶部 FilterBar**：多字段组合筛选、清除筛选、切换视图。

### 4.3 排序
点击表头字段（如 \`Item Number\`、\`Final Price\`、\`Carton Qty\`）切换升降序。

### 4.4 分页
底部 Pagination 组件，可跳页、切换每页数量（默认 50）。

### 4.5 单品详情
- **List 中点击行** 或 **Grid 中点击卡片** 打开 Product Drawer。
- Drawer 支持查看全部字段、切换 Variants、放大图片、编辑（Admin/Office）。

### 4.6 编辑产品（Admin/Office）
- 在 Drawer 中点击 "Edit" 修改字段，保存后同步回 FileMaker。
- 上传 / 替换产品照片：点击图片区域上传，系统自动缩放并存入对象存储。

### 4.7 Block / 屏蔽产品（Admin/Office）
- 在 Drawer 中点击 "Block"，填写理由。
- Block 后该产品在其他角色的 Dashboard 上被标记 / 隐藏。
- 可随时 Unblock。

### 4.8 批量导出（Dashboard 顶部菜单）
- **Export Selected → Quotation PDF / Excel**：将选中行按 Quotation 模板导出。
- **Export Selected → Best Selling Print**：生成 Best Selling 打印页。
- 需要先勾选行前的复选框。

---

## 五、购物车 / 报价管理 Carts & Quotes

### 5.1 购物车概念
每位用户可创建 **多个购物车（Cart / Quote）**，每个购物车拥有：
- 名称（例：客户名、日期）
- 关联 Customer（可选，用于自动套用系数）
- Price Coefficient（价格系数）
- Currency（币种）
- 独立的 items 列表

### 5.2 创建购物车
- 在 Dashboard 顶部 → "New Cart"，或 \`/carts\` 列表页 → "New Cart"。
- 输入名称、选择客户（可跳过）、确认。

### 5.3 添加商品到购物车
- 在 Dashboard 或 Product Drawer 中点击 "Add to Cart"，选择目标购物车。
- 数量默认按 Carton Qty，可自定义。

### 5.4 购物车页面 \`/cart/:id\`
- **CartHeader**：显示名称、客户、系数、币种、总计。
- **CartItemsTable / Grid**：可切换 Table 或 Grid 展示。
- **CartActionsBar**：导出 Quotation PDF / Excel、Proforma Invoice、GT Offer Sheet、Email Quote。
- **QuotesListPanel**：右侧列出用户所有购物车，可快速切换。

### 5.5 编辑购物车项
- **数量 / Carton Qty**：直接在表格中修改。
- **Final Price**：Sales 及以上角色可修改。
- **Price Coefficient**：修改后，购物车内所有价格自动按系数重算。
- **删除条目**：仅 Admin/Office 可用（Sales 需请 Office 协助）。

### 5.6 清空购物车（Admin/Office）
CartHeader → "Clear Cart" → 二次确认。

### 5.7 打印视图
- \`/cart/:id/grid-print\`：网格打印页，适合客户展示。
- \`/cart/:id/labels-print\`：标签打印页。

---

## 六、客户管理 Customer Management

> 权限：Admin / Office

\`/customers\` 页面维护客户档案，用于报价时自动套用价格逻辑。

### 6.1 客户字段
- **Name**：客户名（唯一）
- **Currency**：币种（USD / CAD / RMB 等）
- **Price Coefficient**：价格系数（例：2.0 = 报价 = 原价 × 2）
- **Freight per Skid / Inland Freight**：单托盘内陆运费（用于 Proforma 自动计算）
- **备注**

### 6.2 操作
- 新建：右上角 "+ New Customer"
- 编辑：行内点击铅笔图标
- 删除：删除前请先解绑关联的购物车

### 6.3 在购物车中关联客户
- 打开购物车 → Header → "Select Customer" → 选择后系数 / 币种 / 运费自动带入。

---

## 七、Giant Tiger 专属功能

> 权限：Admin / Office / GT Sales

### 7.1 GT SKU 列
- Dashboard 中 GT SKU 列**位于 Item Number 前面**。
- 普通 Sales / Customer 完全看不到 GT SKU（列隐藏、搜索无效、API 也会剥离字段）。

### 7.2 GT Selling History \`/gt-selling-history\`
- 上传 GT Sell-Through Report（Excel）。
- 系统按 GT SKU 匹配 FileMaker 产品，展示历史销售数据。
- 非 GT 权限用户访问会 403 并跳转。

### 7.3 GT Offer Sheet 导出
- 在购物车 → CartActionsBar → "Export GT Offer Sheet"。
- 系统将购物车条目注入 GT 官方 \`.xlsm\` 模板（ActiveX 控件保留完整）。
- **每个 Variant** 单独生成一份 \`.xlsm\`，最后打包为 ZIP 下载。
- 长宽高（cm）自动转换为英寸，毛重（kg）转换为磅。

---

## 八、中国报价系统联动（China Quote）

> 权限：**仅 Admin / Office 可见**

### 8.1 功能说明
Dashboard / Product Grid 中的 Item Number 对 Admin/Office 显示为**可点击链接**。点击后弹出 Modal，实时查询宁波 BetterHome 报价系统（\`ningbowise.better-home.ca\`），返回该产品的 RMB 成本、供应商、更新时间等信息。

### 8.2 模糊匹配
- 系统会自动去除符号（\`-\`, \`_\`, 空格等）、忽略大小写。
- 支持部分匹配：例如 \`AB-123\` 可匹配到 \`ab123\`、\`AB123X\` 等。

### 8.3 其他角色
Sales / GT Sales / Customer 只会看到 Item Number 的普通文本，无链接、无 Modal。

---

## 九、移动端与条码扫描

### 9.1 PWA 安装
- iOS Safari：分享 → "添加到主屏幕"。
- Android Chrome：菜单 → "安装应用"。

### 9.2 Mobile Products \`/mobile/products\`
移动端优化的产品浏览页，卡片式，适合仓库 / 门店。

### 9.3 Barcode 扫描
- 点击顶部相机图标打开 BarcodeScannerModal。
- 允许摄像头权限后，将条码对准扫描框，自动匹配产品。
- 不支持扫码时可手动输入。

### 9.4 Mobile Cart \`/mobile/cart\`
移动端购物车视图。

---

## 十、邮件报价与公开报价页

### 10.1 发送邮件（Admin/Office/Sales/GT Sales）
- 购物车 → CartActionsBar → "Email Quote"。
- 输入客户邮箱、可选留言，发送。
- 系统通过 Resend 发送包含公开链接的邮件。

### 10.2 公开报价页 \`/quote/:token\`
- 客户无需登录即可查看报价（价格已按系数呈现）。
- 支持在线导出 PDF。

---

## 十一、数据同步 Sync & 备份 Backup

> 权限：Admin / Office，位于 \`/settings\`

### 11.1 自动同步
- 每日 2 次定时从 FileMaker 拉取最新 \`main products\`。
- 包含图片下载 + 归一化处理 + 对象存储上传。

### 11.2 手动同步 "Refresh Now"
Settings → Sync Section → "Refresh Now"。用于紧急更新场景。

### 11.3 备份 Backup Section
- 下载当前 MongoDB 备份（JSON / ZIP）。
- 建议每周下载一次留存。

---

## 十二、用户管理 User Management

> 权限：Admin / Office，位于 \`/settings\` → User Management Section

### 12.1 新建用户
- Email、姓名、角色（admin/office/sales/gt_sales/customer）。
- 系统生成初始密码，用户首次登录后应立即修改。
- **本系统不允许自助注册**，仅由管理员创建。

### 12.2 编辑用户
- 修改角色、启用 / 禁用。
- 重置密码：生成新的一次性密码。

### 12.3 删除用户
- 删除前请确认没有活跃的购物车关联。

### 12.4 API Tokens \`/settings\` → ApiTokensSection
- 为外部集成生成长期 Token（Admin 可用）。

---

## 十三、个人设置 Settings

\`/settings\`（部分区块按角色显示）：
- **基础信息**：修改姓名、密码。
- **User Management**（Admin/Office）
- **Sync**（Admin/Office）
- **Backup**（Admin/Office）
- **API Tokens**（Admin）

---

## 十四、常见问题 FAQ

**Q1：为什么我的搜索找不到 GT SKU？**  
A：只有 Admin / Office / GT Sales 才能通过 GT SKU 搜索，其他角色 GT SKU 完全不可见。

**Q2：点击 Item Number 没反应？**  
A：China Quote 链接仅对 Admin / Office 可用。其他角色只显示普通文本。

**Q3：GT Offer Sheet 打开后 ActiveX 报错？**  
A：请用 **Excel 桌面版**（Windows）打开，不能使用 Excel Online / Mac 版。

**Q4：为什么价格和 FileMaker 不一致？**  
A：Customer / 购物车带有 Price Coefficient，系统会按 \`原价 × 系数\` 显示。

**Q5：数据看起来不是最新的？**  
A：系统每天同步 2 次；如需实时，请让 Admin / Office 点击 Refresh Now。

**Q6：密码忘了？**  
A：登录页 "Forgot password?"，检查邮箱重置链接（60 分钟内有效）。

**Q7：可以扫码入库吗？**  
A：目前扫码仅用于**查询**产品，不做入库操作。

**Q8：可以在手机上导出 PDF 吗？**  
A：可以，但由于文件较大，建议 WiFi 环境下操作。

---

## 联系与反馈

如遇 Bug 或需求，请联系管理员 Jack Yang，或发邮件至系统管理员邮箱。

> 本手册版本：2026-02 · 系统持续更新中
`;

export const HELP_EN_SALES = `# BetterHome Inventory — User Manual (Sales Role)

> Version: 2026-02  
> Audience: **Sales role only**  
> Language: English

This manual covers **only** the features and workflows available to users with the **Sales** role. If you need features described elsewhere (GT SKU, RMB cost lookup, Customer Management, User Management, product editing), please contact your Admin or Office team — those areas are outside the Sales role's permissions.

---

## Table of Contents

1. [What Sales Can Do](#1-what-sales-can-do)
2. [Signing In](#2-signing-in)
3. [Product Dashboard](#3-product-dashboard)
4. [Searching & Filtering](#4-searching--filtering)
5. [Viewing a Product](#5-viewing-a-product)
6. [Managing Multiple Carts / Quotes](#6-managing-multiple-carts--quotes)
7. [Editing Cart Items](#7-editing-cart-items)
8. [Exporting Quotations](#8-exporting-quotations)
9. [Emailing a Quote](#9-emailing-a-quote)
10. [Mobile Use & Barcode Scanning](#10-mobile-use--barcode-scanning)
11. [Personal Settings](#11-personal-settings)
12. [What Sales Cannot Do](#12-what-sales-cannot-do)
13. [FAQ](#13-faq)

---

## 1. What Sales Can Do

As a **Sales** user, you can:

- Browse, search and filter the product catalog (cached from FileMaker).
- Look up products by barcode (including mobile camera scanning).
- Create and manage **multiple private carts / quotes** — each with its own name and price coefficient.
- Add items to any of your carts and adjust quantity, Final Price and Price Coefficient.
- Export **Quotation** (PDF/Excel) and **Proforma Invoice** from a cart.
- **Email a quote** to a customer with a public link.
- Install the app as a PWA on your phone and use camera barcode scanning.

You cannot edit products, manage customers/users, see GT SKU / GT reports, view RMB cost data, or delete cart items. See [Section 12](#12-what-sales-cannot-do) for the full list.

---

## 2. Signing In

1. Open the login page \`/login\`.
2. Enter the email and password provided by your Admin.
3. Click **Sign In**.
4. **Forgot password?** Click the link on the login page, enter your email — a reset link will be sent to you (valid for 60 minutes, one-time use).
5. **Sign out**: click your avatar in the top-right corner → **Logout**.

> **Security**: After 5 failed login attempts your account is locked for 15 minutes.

---

## 3. Product Dashboard

After signing in you land on the **Dashboard** — a cached view of BetterHome's "main products" catalog.

### 3.1 View Modes
- **List View** — dense, sortable table for scanning many items quickly.
- **Grid View** — card layout with product photos, ideal for showing customers.

Toggle between them using the buttons in the top filter bar.

### 3.2 Layout
- **Left sidebar** — category and quick filters (Best Selling, Blocked, Inventory Type…).
- **Top filter bar** — search box, multi-field filters, view toggle, "Add to Cart" batch action.
- **Main area** — the list or grid of products.
- **Bottom** — pagination controls (jump to page, change page size).

---

## 4. Searching & Filtering

### 4.1 Search Box
The top search input searches across:
- Item Number
- Description
- Barcode

> **Note**: GT SKU is **not** searchable in the Sales role (it is hidden from your view entirely).

### 4.2 Sidebar Filters
- **Category** — pick one or more.
- **Best Selling** — narrow to items flagged as best sellers.
- **Blocked** — hide or focus on items that Admin/Office have blocked.
- **Inventory Type** — filter by type (e.g., exclude discontinued).

### 4.3 Sorting
Click any column header in List View to toggle ascending/descending sort (Item Number, Description, Final Price, Carton Qty, etc.).

### 4.4 Pagination
Use the pagination bar at the bottom to change page or page size (default 50 per page).

---

## 5. Viewing a Product

- **List view**: click a row.
- **Grid view**: click a card.

The **Product Drawer** opens on the right showing:
- All product fields (read-only for Sales).
- Product image (click to enlarge).
- Variants selector (if multiple variants exist).
- **Add to Cart** button.

> Sales cannot edit product fields, upload photos, or Block/Unblock products.

---

## 6. Managing Multiple Carts / Quotes

You can maintain as many carts (quotes) as you need — one per customer, per project, per date, etc.

### 6.1 View Your Carts
Go to \`/carts\` — you see all your carts as cards with name, item count and total.

### 6.2 Create a New Cart
- Click **New Cart** (on \`/carts\` or from the Dashboard top bar).
- Enter a name (e.g. \`ACME - Feb 2026\`).
- Optionally choose a currency and set a Price Coefficient.
- Click **Create**.

### 6.3 Rename a Cart
On the cart card → click the pencil icon → enter a new name.

### 6.4 Switch Between Carts
- From \`/carts\`, click any cart card to open it.
- Inside a cart, the right-hand **Quotes List Panel** lists all your carts — click to switch.

### 6.5 Add Items to a Cart
- On the Dashboard: click **Add to Cart** on a row/card or from the Product Drawer.
- Select the target cart.
- Adjust the initial quantity if needed.

### 6.6 Cart Page \`/cart/:id\`
- **Header** — cart name, currency, price coefficient, total.
- **Items table/grid** — every item added, with quantity, unit price, line total.
- **Actions bar** — Export, Email Quote.
- **Right-side Quotes Panel** — quick switch between your carts.

---

## 7. Editing Cart Items

As Sales you **can**:
- Change **Carton Qty / Quantity** — click the field and type a new value.
- Change **Final Price** — override the calculated price for a single line.
- Change the cart's **Price Coefficient** — all prices recalculate automatically (price = base × coefficient).

You **cannot**:
- Remove individual line items from a cart.
- Clear the entire cart.

> If you need to remove or clear items, ask an Office user to do it for you.

---

## 8. Exporting Quotations

From the cart's **Actions Bar** you can export:

### 8.1 Quotation
- **Quotation PDF** — customer-ready PDF with logo, items, prices, totals.
- **Quotation Excel** — editable \`.xlsx\` version.

### 8.2 Proforma Invoice
- **Proforma PDF / Excel** — includes freight/inland freight calculations when the cart has that data.

### 8.3 Dashboard Bulk Export
On the Dashboard you can also select multiple products (via the checkboxes) and use the top **Export** menu to generate a Quotation PDF/Excel directly from the selection, without adding them to a cart.

> GT Offer Sheet export is **not available** in the Sales role.

---

## 9. Emailing a Quote

1. Open the cart you want to send.
2. Actions bar → **Email Quote**.
3. Enter the customer's email address and an optional message.
4. Click **Send**.
5. The customer receives an email (sent via Resend) with a **public link** to a read-only quote page — no login required on their side.
6. They can view the quote in the browser and download the PDF themselves.

Public quote link format: \`/quote/:token\` (token is unique per email send).

---

## 10. Mobile Use & Barcode Scanning

The app is a PWA — you can install it on your phone's home screen.

### 10.1 Install as PWA
- **iOS Safari**: Share → *Add to Home Screen*.
- **Android Chrome**: menu → *Install app*.

### 10.2 Mobile Products \`/mobile/products\`
A phone-optimized card view for browsing the catalog while walking through a store or warehouse.

### 10.3 Barcode Scanning
- Tap the **camera icon** at the top of the mobile view.
- Grant camera permission.
- Point the camera at a product barcode — the app looks it up automatically.
- If your device can't scan, type the barcode manually in the search box.

### 10.4 Mobile Cart \`/mobile/cart\`
Mobile-optimized cart page — same edits are possible (quantity, price, coefficient).

---

## 11. Personal Settings

Open \`/settings\`. As Sales you can:
- Update your **display name**.
- Change your **password**.

You will **not** see: User Management, Sync, Backup, or API Tokens sections — those are Admin/Office only.

---

## 12. What Sales Cannot Do

For clarity, the following are explicitly **outside** the Sales role. If you need any of these, contact your Admin or Office team:

- Create, edit, or upload photos for products (FileMaker records).
- Block / Unblock products.
- Delete cart items or clear a cart.
- View **GT SKU**, **GT Selling History**, or export **GT Offer Sheet**.
- View **China Quote / RMB cost** data (Item Number stays plain text for Sales).
- Manage **Customers** (currency, price coefficient, freight profiles).
- Manage **Users** (create/edit/delete accounts, reset other users' passwords).
- Trigger manual FileMaker **Sync** or download **Backups**.
- Generate long-lived **API tokens**.

---

## 13. FAQ

**Q1 — I can't find a product by its GT SKU. Why?**  
The GT SKU field is hidden for the Sales role. Search by Item Number, Description or Barcode instead.

**Q2 — Item Number is not clickable — is something broken?**  
No. The clickable China Quote (RMB cost) link is only shown to Admin and Office. Sales sees plain text.

**Q3 — I want to remove one item from my cart.**  
Ask an Office user to do it — Sales can't delete individual cart lines.

**Q4 — I forgot my password.**  
Click **Forgot password?** on the login page. The reset link is valid for 60 minutes.

**Q5 — My price looks different from FileMaker.**  
Your cart or the customer profile has a **Price Coefficient**. All prices shown are \`base price × coefficient\`.

**Q6 — Can I email a quote to a customer without them having an account?**  
Yes — the Email Quote feature sends a public link to a read-only quote page.

**Q7 — The data looks out of date.**  
The catalog syncs from FileMaker twice a day automatically. If you need it refreshed right now, ask Admin/Office to click *Refresh Now* in Settings.

**Q8 — Can I install the app on my phone?**  
Yes — see [Section 10.1](#101-install-as-pwa).

**Q9 — Where can I see all my carts in one place?**  
Go to \`/carts\`. Each cart card shows its name, currency, item count and total.

**Q10 — Can I export a quote without adding items to a cart?**  
Yes — select rows on the Dashboard with the checkboxes and use the top **Export** menu.

---

## Support

If something isn't working or you need access to a feature outside the Sales role, please contact your Admin (Jack Yang) or the Office team.

> Manual version: 2026-02 · Sales role scope only
`;
